import { BoxProgrammationTypeOf } from "@modules/boxProgrammation/boxProgrammationTypes"
import { BoxProgrammationSession } from "@modules/boxSlot/services/boxSlotDto"
import { Service } from "@modules/core/services/serviceType"
import {
  ProgramInfiniteSession,
  ProgramInfiniteSessionDraft,
  ProgramOnOffSession,
  ProgramOnOffSessionDraft,
} from "@modules/programSession/programSessionTypes"
import { ProgramInfiniteSessionApi } from "@modules/programSession/services/programInfiniteSessionApi"
import { ProgramOnOffSessionApi } from "@modules/programSession/services/programOnOffSessionApi"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { QueryClient } from "@tanstack/react-query"
import { observable } from "micro-observables"

export class ProgramSessionService implements Service {
  private copySession: ProgramInfiniteSession | ProgramOnOffSession | BoxProgrammationSession | undefined
  private _copyWeek = observable<
    | {
        programType: typeof ProgramInfiniteTypeOf
        originProgramId: string
        originProgramName: string
        originStartDate: string
        originEndDate: string
      }
    | {
        programType: typeof ProgramOnOffTypeOf
        originProgramId: string
        originProgramName: string
        originStartIndex: number
        originEndIndex: number
      }
    | undefined
  >(undefined)
  readonly copyWeek = this._copyWeek.readOnly()

  constructor(
    private programInfiniteSessionApi: ProgramInfiniteSessionApi,
    private programOnOffSessionApi: ProgramOnOffSessionApi,
    private queryClient: QueryClient,
  ) {}

  setCopySession = (sessionToCopy: ProgramInfiniteSession | ProgramOnOffSession | BoxProgrammationSession) => {
    this.copySession = sessionToCopy
  }

  getCopySession = () => {
    return this.copySession
  }

  setInfiniteWeekToCopy = (
    programType: typeof ProgramInfiniteTypeOf,
    originProgramId: string,
    originProgramName: string,
    originStartDate: string,
    originEndDate: string,
  ) => {
    this._copyWeek.set({
      programType,
      originProgramId,
      originProgramName,
      originStartDate,
      originEndDate,
    })
  }

  setOnOffWeekToCopy = (
    programType: typeof ProgramOnOffTypeOf,
    originProgramId: string,
    originProgramName: string,
    originStartIndex: number,
    originEndIndex: number,
  ) => {
    this._copyWeek.set({
      programType,
      originProgramId,
      originProgramName,
      originStartIndex,
      originEndIndex,
    })
  }

  clearWeekToCopy = () => {
    this._copyWeek.set(undefined)
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async init(): Promise<void> {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async onLoggedIn(): Promise<void> {}

  isProgramOnOffSessionDraft(
    session: ProgramInfiniteSessionDraft | ProgramOnOffSessionDraft,
  ): session is ProgramOnOffSessionDraft {
    return (session as ProgramOnOffSessionDraft).restDay !== undefined
  }

  async createSession(session: ProgramInfiniteSessionDraft | ProgramOnOffSessionDraft): Promise<string | undefined> {
    if (this.isProgramOnOffSessionDraft(session)) {
      const programSession = await this.programOnOffSessionApi.createSession(session)
      return programSession.id
    } else {
      const programSession = await this.programInfiniteSessionApi.createSession(session)
      return programSession.id
    }
  }

  async updateSession(
    id: string,
    session: ProgramInfiniteSessionDraft | ProgramOnOffSessionDraft,
  ): Promise<string | undefined> {
    const clearSessionCache = async () => {
      await this.queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "programSession" && query.queryKey[1] === id,
      })
    }

    if (this.isProgramOnOffSessionDraft(session)) {
      const programSession = await this.programOnOffSessionApi.updateSession(id, session)
      await clearSessionCache()
      return programSession.id
    } else {
      const programSession = await this.programInfiniteSessionApi.updateSession(id, session)
      await clearSessionCache()
      return programSession.id
    }
  }

  async deleteSession(
    id: string,
    programId: string,
    programType: typeof ProgramInfiniteTypeOf | typeof ProgramOnOffTypeOf | typeof BoxProgrammationTypeOf,
  ) {
    if (programType === ProgramInfiniteTypeOf) await this.programInfiniteSessionApi.deleteSession(id)
    else if (programType === ProgramOnOffTypeOf) await this.programOnOffSessionApi.deleteSession(id, programId)
  }

  async changeSessionIndex(id: string, position: number) {
    await this.programInfiniteSessionApi.changeSessionIndex(id, position)
  }

  async copyWeekFromAnInfiniteProgramToAnother(
    originProgramId: string,
    destinationProgramId: string,
    originStartDate: string,
    originEndDate: string,
    destinationStartDate: string,
    overwrite: boolean = false,
  ) {
    await this.programInfiniteSessionApi.copyWeekFromAnInfiniteProgramToAnother(
      originProgramId,
      destinationProgramId,
      originStartDate,
      originEndDate,
      destinationStartDate,
      overwrite,
    )
  }

  async copyWeekFromAnOnOffProgramToAnother(
    originProgramId: string,
    destinationProgramId: string,
    originStartIndex: number,
    originEndIndex: number,
    destinationStartIndex: number,
    overwrite: boolean = false,
  ) {
    await this.programOnOffSessionApi.copyWeekFromAnOnOffProgramToAnother(
      originProgramId,
      destinationProgramId,
      originStartIndex,
      originEndIndex,
      destinationStartIndex,
      overwrite,
    )
  }
}
